import { useState } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2, Plus } from "lucide-react";
import moment from "moment-timezone";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { useListCertificateTemplates, useDeleteCertificateTemplate, type CertificateTemplate } from "@/lib/api/birthdayFundCertificates";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { GenerateLoadingOrError } from "@/lib/utils";

export function BirthdayFundCertificatesDashboard() {
  const { toast } = useToast();
  const templatesRes = useListCertificateTemplates();
  const deleteCertificateTemplate = useDeleteCertificateTemplate();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadingOrErrorUi = GenerateLoadingOrError([templatesRes]);

  if (loadingOrErrorUi) {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        {loadingOrErrorUi}
      </section>
    );
  }

  const templates = templatesRes.data?.data?.templates || [];

  const handleDelete = async (templateId: string) => {
    if (deletingId) return;
    
    setDeletingId(templateId);
    try {
      const result = await deleteCertificateTemplate(templateId);
      if (!result.success) {
        throw result;
      }

      toast({
        title: "Template Deleted",
        description: "Certificate template has been deleted successfully.",
      });
    } catch (error: any) {
      console.error("Failed to delete certificate template", error);
      toast({
        title: "Failed to Delete Template",
        description: error?.message || "An error occurred while deleting the template.",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const columns: DataTableColumn<CertificateTemplate>[] = [
    {
      id: "actions",
      header: "Actions",
      minSize: 200,
      maxSize: 200,
      cell: ({ row }) => {
        const isDeleting = deletingId === row.original.id;
        return (
          <div className="flex gap-4 flex-row items-center flex-nowrap">
            <Button
              size="icon"
              variant="outline"
              asChild
              disabled={isDeleting}
              className="h-6 w-6"
              title="Create Instance"
            >
              <Link to={`/fund/birthday/certificates/instance/create?templateId=${row.original.id}`}>
                <Plus className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              size="icon"
              variant="outline"
              asChild
              disabled={isDeleting}
              className="h-6 w-6"
              title="Edit Template"
            >
              <Link to={`/fund/birthday/certificates/create?templateId=${row.original.id}`}>
                <Edit className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => handleDelete(row.original.id)}
              disabled={isDeleting}
              className="h-6 w-6"
              title="Delete Template"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
      disableRowClick: true,
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: "location_name",
      accessorKey: "location_name",
      header: "Location Name",
      minSize: 200,
      maxSize: 300,
      cell: (info) => info.getValue() as string,
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      minSize: 140,
      maxSize: 160,
      cell: (info) => moment(info.getValue() as string).format("MM/DD/YYYY h:mm A"),
    },
    {
      id: "updated_at",
      accessorKey: "updated_at",
      header: "Last Updated",
      minSize: 140,
      maxSize: 160,
      cell: (info) => moment(info.getValue() as string).format("MM/DD/YYYY h:mm A"),
    },
  ];

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
      <DataTable
        className="max-h-[calc(100vh-250px)] overflow-y-auto shadow-lg"
        data={templates}
        columns={columns}
        stickyHeaders
        enableColumnVisibility
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnResizing
        enableExport
        filterStyle='select'
        filterMode='internal'
        emptyMessage="No certificate templates found"
        defaultSorting={[
          { id: "location_name", desc: false },
        ]}
      />
    </section>
  );
}
